import { useState, useEffect } from 'react';
import './candidateCertificates.css';
import { faChevronDown, faChevronUp, faPen, faPlus, faMinusCircle, faPlusSquare, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Container, Row, Modal, Form } from 'react-bootstrap';
import CertificationsIcon from '../../../../assets/images/Ic_certificate.png';
import CandidateCertificationInfoPanel from './candidateCertificateInfoPanel';
import { CandidatePostLoginService } from '../../../../services/candidatePostLoginService';
import ReactDatePicker from 'react-datepicker';
import CloseIcon from '../../../../assets/images/Ic_close.png';
const CandidateCertificateInfo = ({ certificateDocs = [], candidateId, candidateData, fetchCandidateData }) => {
  const [showAll, setShowAll] = useState(false);
  const [editedCertificates, setEditedCertificates] = useState([]);
  const [editingIndexes, setEditingIndexes] = useState([]);
  const [originalCertificates, setOriginalCertificates] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [newCertificates, setNewCertificates] = useState([]);
  const [tempEditData, setTempEditData] = useState(null);
  const [savingCertificates, setSavingCertificates] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [certFieldErrors, setCertFieldErrors] = useState({});
  const [removedEditedCertificateIndexes, setRemovedEditedCertificateIndexes] = useState([]);
  const [isCurrentCertificateRemoved, setIsCurrentCertificateRemoved] = useState(false);

  const isEditing = editingIndexes.length === 1 && tempEditData;
  const isEditMode = isEditing && !removedEditedCertificateIndexes.includes(editingIndexes[0]);


  useEffect(() => {
    setEditedCertificates(Array.isArray(certificateDocs) ? certificateDocs : []);
  }, [certificateDocs]);

  const visibleCertificates = showAll ? editedCertificates : editedCertificates.slice(0, 2);

  const validateCertificateField = (key, value) => {
    if (key === 'title' || key === 'universityOrCollege') {
      if (value.trim().length > 0 && (value.trim().length < 2 || value.trim().length > 100)) {
        return `${key === 'title' ? 'Certificate Title' : 'Issued By'} must be 2–100 characters`;
      }
    }
    return '';
  };

  const validateDateRange = (startDate, endDate) => {
    if (startDate && endDate && endDate < startDate) {
      return 'End date should not be earlier than start date';
    }
    return '';
  };

  const handleCertificateChange = (index, field, value) => {
    if (!editedCertificates[index]) return;
    setEditedCertificates(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    setCertFieldErrors(prev => {
      const newErrors = { ...prev };

      if (['title', 'universityOrCollege'].includes(field)) {
        const error = validateCertificateField(field, value);
        if (error) newErrors[`${index}_${field}`] = error;
        else delete newErrors[`${index}_${field}`];
      }

      if (['startDate', 'endDate'].includes(field)) {
        const cert = editedCertificates[index];
        const start = field === 'startDate' ? value : cert.startDate;
        const end = field === 'endDate' ? value : cert.endDate;
        const dateError = validateDateRange(start, end);
        if (dateError) newErrors[`${index}_date`] = dateError;
        else delete newErrors[`${index}_date`];
      }

      return newErrors;
    });
  };

  const handleEdit = (index) => {
    setOriginalCertificates(prev => ({
      ...prev,
      [index]: { ...editedCertificates[index] }
    }));
    setEditingIndexes([index]);
    setTempEditData({ ...editedCertificates[index] });
    setRemovedEditedCertificateIndexes([]);
    setIsCurrentCertificateRemoved(false);
    setModalVisible(true);
  };


  const handleAddForm = () => {
    setEditingIndexes([]);
    setTempEditData(null);
    setNewCertificates([{ title: '', universityOrCollege: '', startDate: '', endDate: '' }]);
    setModalVisible(true);
  };

  const handleNewFieldChange = (index, field, value) => {
    const updatedCertificates = [...newCertificates];
    updatedCertificates[index] = { ...updatedCertificates[index], [field]: value };
    setNewCertificates(updatedCertificates);

    setCertFieldErrors(prev => {
      const newErrors = { ...prev };

      if (['title', 'universityOrCollege'].includes(field)) {
        const error = validateCertificateField(field, value);
        if (error) newErrors[`${index}_${field}`] = error;
        else delete newErrors[`${index}_${field}`];
      }

      if (['startDate', 'endDate'].includes(field)) {
        const cert = updatedCertificates[index];
        const start = field === 'startDate' ? value : cert.startDate;
        const end = field === 'endDate' ? value : cert.endDate;
        const dateError = validateDateRange(start, end);
        if (dateError) newErrors[`${index}_date`] = dateError;
        else delete newErrors[`${index}_date`];
      }

      return newErrors;
    });
  };


  const handleRemoveCertificate = (index) => {
    setNewCertificates(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

    setCertFieldErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${index}_`)) {
          delete newErrors[key];
        }
      });

      const updatedErrors = {};
      Object.entries(newErrors).forEach(([key, value]) => {
        const match = key.match(/^(\d+)_/);
        if (match) {
          const oldIndex = Number(match[1]);
          if (oldIndex > index) {
            const newKey = key.replace(/^(\d+)_/, `${oldIndex - 1}_`);
            updatedErrors[newKey] = value;
          } else {
            updatedErrors[key] = value;
          }
        } else {
          updatedErrors[key] = value;
        }
      });

      return updatedErrors;
    });
  };


  const handleSave = async () => {
    setSavingCertificates(true);
    const hasCertErrors = Object.values(certFieldErrors).some(err => err && err.trim() !== '');
    if (hasCertErrors) {
      setErrorMessage("Please fix the field-level validation errors before saving.");
      setSavingCertificates(false);
      return;
    }

    let updatedCertificates = [...editedCertificates];

    if (editingIndexes.length === 1 && tempEditData) {
      const index = editingIndexes[0];

      updatedCertificates[index] = { ...tempEditData };
    }

    updatedCertificates = updatedCertificates.filter((_, i) => !removedEditedCertificateIndexes.includes(i));


    const validatedNewCertificates = newCertificates.map((cert) => ({
      title: cert.title?.trim() || '',
      universityOrCollege: cert.universityOrCollege?.trim() || '',
      startDate: cert.startDate ? new Date(cert.startDate).toISOString() : null,
      endDate: cert.endDate ? new Date(cert.endDate).toISOString() : null,
    }));


    const validatedEditedCertificates = updatedCertificates.map((cert, i) => ({
      title: cert.title?.trim() || '',
      universityOrCollege: cert.universityOrCollege?.trim() || '',
      startDate: cert.startDate ? new Date(cert.startDate).toISOString() : null,
      endDate: cert.endDate ? new Date(cert.endDate).toISOString() : null,
    }));

    const finalCertificates = [...validatedEditedCertificates, ...validatedNewCertificates];



    try {
      // await CandidatePostLoginService.submitCertifications(finalCertificates);
      await CandidatePostLoginService.submitPanelByType('CERTIFICATIONS', {
        inputName: 'certifications',
        inputList: finalCertificates
      });
      setModalVisible(false);
      setNewCertificates([]);
      setTempEditData(null);
      setEditingIndexes([]);
      setOriginalCertificates({});
      setRemovedEditedCertificateIndexes([]);

      await fetchCandidateData();

    } catch (error) {
      console.error('Failed to save education:', error);
    } finally {
      setSavingCertificates(false);
    }
  };


  const handleCancel = () => {
    if (editingIndexes.length === 1) {
      const index = editingIndexes[0];
      setEditedCertificates(prev => {
        const updated = [...prev];
        if (originalCertificates[index]) {
          updated[index] = originalCertificates[index];
        }
        return updated;
      });
      setEditingIndexes([]);
      setOriginalCertificates(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
    setNewCertificates([]);
    setCertFieldErrors({});
    setRemovedEditedCertificateIndexes([]);
    setTempEditData(null);
    setModalVisible(false);
  };

  const handleRemoveEditedCertificate = (index) => {
    setRemovedEditedCertificateIndexes(prev => {
      if (!prev.includes(index)) {
        return [...prev, index];
      }
      return prev;
    });
    setIsCurrentCertificateRemoved(true);
  };

  const getGlobalCertificateNumber = (index, isNew = false) =>
    (editedCertificates?.length || 0) + (isNew ? index + 1 : 0);

  return (
    <>
      {errorMessage && (
        <div className="custom-alert-overlay">
          <div className="custom-alert-box">
            <div className="custom-alert-content">
              <p>{errorMessage}</p>
              <button className="custom-alert-button" onClick={() => setErrorMessage('')}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {modalVisible && (editingIndexes.length === 1 || newCertificates.length > 0) && (

        <Modal show={modalVisible} onHide={handleCancel} centered backdrop="static" className="candidate-custom-modal" dialogClassName="candidate-custom-modal-dialog">
          <Modal.Header className="d-flex justify-content-between align-items-center">
            <Modal.Title>{isEditing ? 'Edit Certificate Info' : 'Add Certificate Info'}</Modal.Title>
            <img
              src={CloseIcon}
              alt="Close"
              onClick={handleCancel}
              style={{
                cursor: 'pointer',
                width: '24px',
                height: '24px',
              }}
            />
          </Modal.Header>
          <Modal.Body>
            <style>
              {`
    .react-datepicker__input-container input {
      border: 1px solid #ced4da !important;
      box-shadow: none !important;
    }

    .react-datepicker__input-container input:focus {
      border: 1px solid #ced4da !important;
      box-shadow: none !important;
      outline: none;
    }
  `}
            </style>
            {isEditing && (
              <>
                <h5 className="mb-3">
                  Certificate – {editingIndexes.length === 1 ? editingIndexes[0] + 1 : ''}
                </h5>
                {isCurrentCertificateRemoved && (
                  <div
                    className="alert alert-warning mt-2 mb-3"
                    style={{ padding: '8px 12px', fontSize: '0.9rem' }}
                  >
                    This certificate will be removed once you click save.
                  </div>
                )}
                {!isCurrentCertificateRemoved && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Certificate Title</label>
                      <input
                        style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                        type="text"
                        spellCheck='false'
                        className={`form-control ${certFieldErrors[`edit_title`] ? 'is-invalid' : ''}`}
                        value={tempEditData.title || ''}
                        onChange={(e) => {
                          setTempEditData({ ...tempEditData, title: e.target.value });
                          const error = validateCertificateField('title', e.target.value);
                          setCertFieldErrors(prev => ({ ...prev, edit_title: error }));
                        }}
                      />
                      {certFieldErrors[`edit_title`] && (
                        <div className="invalid-feedback">{certFieldErrors[`edit_title`]}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Issued By (University / College Name)</label>
                      <input
                        style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                        type="text"
                        spellCheck='false'
                        className={`form-control ${certFieldErrors[`edit_universityOrCollege`] ? 'is-invalid' : ''}`}
                        value={tempEditData.universityOrCollege || ''}
                        onChange={(e) => {
                          setTempEditData({ ...tempEditData, universityOrCollege: e.target.value });
                          const error = validateCertificateField('universityOrCollege', e.target.value);
                          setCertFieldErrors(prev => ({ ...prev, edit_universityOrCollege: error }));
                        }}
                      />
                      {certFieldErrors[`edit_universityOrCollege`] && (
                        <div className="invalid-feedback">{certFieldErrors[`edit_universityOrCollege`]}</div>
                      )}
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label>Start MM/YYYY</Form.Label>
                      <ReactDatePicker
                        selected={tempEditData.startDate ? new Date(tempEditData.startDate) : null}
                        onChange={(val) => {
                          const startDate = val ? val.getTime() : '';
                          setTempEditData({ ...tempEditData, startDate });
                          const error = validateDateRange(startDate, tempEditData.endDate);
                          setCertFieldErrors(prev => ({ ...prev, edit_date: error }));
                        }} showMonthYearPicker
                        dateFormat="MM/yyyy"
                        className={`form-control ${certFieldErrors[`edit_date`] ? 'is-invalid' : ''}`}
                        maxDate={new Date()}
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>End MM/YYYY</Form.Label>
                      <ReactDatePicker
                        selected={tempEditData.endDate ? new Date(tempEditData.endDate) : null}
                        onChange={(val) => {
                          const endDate = val ? val.getTime() : '';
                          setTempEditData({ ...tempEditData, endDate });
                          const error = validateDateRange(tempEditData.startDate, endDate);
                          setCertFieldErrors(prev => ({ ...prev, edit_date: error }));
                        }} showMonthYearPicker
                        dateFormat="MM/yyyy"
                        className={`form-control ${certFieldErrors[`edit_date`] ? 'is-invalid' : ''}`}
                        maxDate={new Date()}
                        autoComplete="off"
                      />
                      {certFieldErrors[`edit_date`] && (
                        <div className="invalid-feedback d-block">{certFieldErrors[`edit_date`]}</div>
                      )}
                    </Form.Group>
                    {isEditMode && (
                      <div className="d-flex justify-content-end mt-3">
                        <span
                          style={{ fontWeight: '500', color: '#F35824', cursor: 'pointer' }}
                          onClick={() => handleRemoveEditedCertificate(editingIndexes[0])}
                        >
                          <FontAwesomeIcon icon={faMinusCircle} />
                        </span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {newCertificates.map((certificate, index) => (
              <div key={`new-certificate-${index}`} className="mb-3">
                <h5>Certificate – {getGlobalCertificateNumber(index, true)}</h5>

                <div className="mb-3">
                  <label className="form-label">Certificate Title </label>
                  <input
                    style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                    type="text"
                    spellCheck='false'
                    className={`form-control ${certFieldErrors[`${index}_title`] ? 'is-invalid' : ''}`}
                    value={certificate.title || ''}
                    onChange={(e) => handleNewFieldChange(index, 'title', e.target.value)}
                  />
                  {certFieldErrors[`${index}_title`] && (
                    <div className="invalid-feedback">{certFieldErrors[`${index}_title`]}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Issued By (University / College Name)</label>
                  <input
                    style={{ boxShadow: 'none', border: '1px solid #ced4da' }}
                    type="text"
                    spellCheck='false'
                    className={`form-control ${certFieldErrors[`${index}_universityOrCollege`] ? 'is-invalid' : ''}`}
                    value={certificate.universityOrCollege || ''}
                    onChange={(e) => handleNewFieldChange(index, 'universityOrCollege', e.target.value)}
                  />
                  {certFieldErrors[`${index}_universityOrCollege`] && (
                    <div className="invalid-feedback">{certFieldErrors[`${index}_universityOrCollege`]}</div>
                  )}
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Start MM/YYYY</Form.Label>
                  <ReactDatePicker
                    selected={certificate.startDate ? new Date(certificate.startDate) : null}
                    onChange={(val) => handleNewFieldChange(index, 'startDate', val.getTime())}
                    showMonthYearPicker
                    dateFormat="MM/yyyy"
                    className={`form-control ${certFieldErrors[`${index}_date`] ? 'is-invalid' : ''}`}
                    maxDate={new Date()}
                    autoComplete="off"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>End MM/YYYY</Form.Label>
                  <ReactDatePicker
                    selected={certificate.endDate ? new Date(certificate.endDate) : null}
                    onChange={(val) => handleNewFieldChange(index, 'endDate', val.getTime())}
                    showMonthYearPicker
                    dateFormat="MM/yyyy"
                    className={`form-control ${certFieldErrors[`${index}_date`] ? 'is-invalid' : ''}`}
                    maxDate={new Date()}
                    autoComplete="off"
                  />
                  {certFieldErrors[`${index}_date`] && (
                    <div className="invalid-feedback d-block">{certFieldErrors[`${index}_date`]}</div>
                  )}
                </Form.Group>
                {isEditMode && (
                  <div className="d-flex justify-content-end mt-3">
                    <span
                      style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
                      onClick={() => handleRemoveCertificate(index)}
                    >
                      <FontAwesomeIcon icon={faMinusCircle} />
                    </span>
                  </div>
                )}
              </div>
            ))}

          </Modal.Body>
          <Modal.Footer>
            <button onClick={handleCancel} className="candidate-profile-edit-cancel" disabled={savingCertificates}>Cancel</button>

            <button onClick={handleSave} className="candidate-profile-edit-save" disabled={savingCertificates}>
              {savingCertificates ? 'Saving...' : 'Save'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
      <Container className="pt-2 certification-container">
        <Row className="align-items-center mb-3">
          <Col className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h3 className="candidate-heading mb-0 me-2">
                Certificates ({editedCertificates.length})
                <img
                  src={CertificationsIcon}
                  alt="Certifications"
                  className="profile-work-experience-nav-icon"
                />
              </h3>
            </div>

            <div
              onClick={handleAddForm}
              style={{ color: '#F35824', cursor: 'pointer', fontWeight: '500' }}
              className='candidate-detail-add-head'
            >
              <FontAwesomeIcon icon={faPlusCircle} /> Add Certificate
            </div>
          </Col>
        </Row>

        <Container>
          {visibleCertificates.filter(Boolean).map((certificate, index) => (
            <div key={`certificate-${index}`} className="d-flex justify-content-between align-items-start mb-3">
              <div style={{ flex: 1 }}>
                <CandidateCertificationInfoPanel
                  colorSeed={Math.random()}
                  certificate={certificate}
                  index={index}
                  isEditing={editingIndexes.includes(index)}
                  onFieldChange={handleCertificateChange}
                />
              </div>
              <div className="ms-3 mt-1">
                <button onClick={() => handleEdit(index)} className="candidate-profile-edit-button">
                  <FontAwesomeIcon icon={faPen} />
                </button>
              </div>
            </div>
          ))}
        </Container>

        {(certificateDocs?.length || 0) > 2 && (
          <Row>
            <Col>
              <a className="certifications-view-all" onClick={() => setShowAll(prev => !prev)}>
                {showAll ? 'View Less' : 'View All'}
                <FontAwesomeIcon icon={showAll ? faChevronUp : faChevronDown} />
              </a>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default CandidateCertificateInfo;